import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { getConsentService } from '../../services/telemetry/granularConsentService';
import type { CookieConsentRecord, CookieCategoryDecisions } from '@little-chef/domain';
import { CookieBanner } from './components/CookieBanner';
import { CookiePreferencesModal } from './components/CookiePreferencesModal';

interface CookieConsentContextValue {
  record: CookieConsentRecord | null;
  acceptAll: () => void;
  declineAll: () => void;
  savePreferences: (categories: CookieCategoryDecisions) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  isPreferencesOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const serviceRef = useRef(getConsentService());
  const [record, setRecord] = useState<CookieConsentRecord | null>(() =>
    serviceRef.current.getRecord(),
  );
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    const unsub = serviceRef.current.subscribe(setRecord);
    return unsub;
  }, []);

  const acceptAll = () => serviceRef.current.acceptAll();
  const declineAll = () => serviceRef.current.declineAll();
  const savePreferences = (categories: CookieCategoryDecisions) =>
    serviceRef.current.savePreferences(categories);
  const openPreferences = () => setIsPreferencesOpen(true);
  const closePreferences = () => setIsPreferencesOpen(false);

  return (
    <CookieConsentContext.Provider
      value={{
        record,
        acceptAll,
        declineAll,
        savePreferences,
        openPreferences,
        closePreferences,
        isPreferencesOpen,
      }}
    >
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col">{children}</div>
        {record === null && <CookieBanner />}
        {isPreferencesOpen && <CookiePreferencesModal onClose={closePreferences} />}
      </div>
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used inside CookieConsentProvider');
  return ctx;
}
